/**
 * Data Lake (HNS) path helpers — S8 Task 8.2, process-agnostic (no Node / DOM / SDK imports; both processes use them).
 * Name rules come from NOTES «S8: live-probed facts» M5: on `jamsqlazsthns` every name below lands exactly as sent when
 * each segment is sent `encodeURIComponent`-encoded, EXCEPT that the service drops a trailing '.', turns '\' into a
 * directory separator and refuses (or URL parsing silently strips) control characters — those are refused here, so a
 * path is either faithful on the wire or never sent.
 */
import type { HnsLocator, HnsPathRef, StorageItem, StorageResourceRef } from '../../types/azureStorage';

/** C0 controls and DEL, checked by code point (a regex literal of control characters trips `no-control-regex`). */
const hasControlCharacter = (text: string): boolean => [...text].some((ch) => { const code = ch.codePointAt(0) ?? 0; return code < 0x20 || code === 0x7f; });

/** `undefined` when one path SEGMENT round-trips through the DFS endpoint; else the user-facing reason. */
export function hnsWireNameProblem(segment: string): string | undefined {
  if (segment === '') return 'A Data Lake name cannot be empty.';
  if (hasControlCharacter(segment)) return 'Data Lake names cannot contain control characters (tab, newline, …).';
  if (segment.includes('\\')) return 'Data Lake names cannot contain "\\" — Azure turns it into a folder separator.';
  if (segment.endsWith('.')) return 'A Data Lake name cannot end with "." — Azure removes the trailing dot.';
  return undefined;
}

export type HnsPathNormalization = { ok: true; path: string } | { ok: false; reason: string };

/**
 * The ONE normalisation of a path typed or sent by a caller: strips one leading and one trailing '/', refuses an empty
 * segment, a '.' / '..' segment and every unfaithful name; `''` (the file-system root) only with `allowRoot`.
 */
export function normalizeHnsPath(input: string, options: { allowRoot?: boolean } = {}): HnsPathNormalization {
  let path = input.startsWith('/') ? input.slice(1) : input;
  if (path.endsWith('/')) path = path.slice(0, -1);
  if (path === '') return options.allowRoot ? { ok: true, path: '' } : { ok: false, reason: 'The path names the file-system root; choose a file or directory inside it.' };
  for (const segment of path.split('/')) {
    if (segment === '') return { ok: false, reason: 'The path contains an empty segment ("//").' };
    if (segment === '.' || segment === '..') return { ok: false, reason: 'The path cannot contain a "." or ".." segment.' };
    const problem = hnsWireNameProblem(segment);
    if (problem) return { ok: false, reason: problem };
  }
  return { ok: true, path };
}

/** `sdd` of a directory SAS: the number of path segments; the file-system root is 0. */
export function hnsDirectoryDepth(path: string): number {
  return path === '' ? 0 : path.split('/').length;
}

/** True when `destination` lies INSIDE the source's subtree (the '/' boundary; the root contains every path of its file system). */
export function isMoveIntoSelf(source: HnsLocator, destination: HnsLocator): boolean {
  if (source.fileSystem !== destination.fileSystem) return false;
  if (source.path === '') return true;
  return destination.path.startsWith(`${source.path}/`);
}

/** The per-segment `encodeURIComponent` spelling the SDK `move()` destination needs (M5: a raw destination is unfaithful). */
export function renameSegmentsEncoded(path: string): string {
  return path.split('/').map((segment) => encodeURIComponent(segment)).join('/');
}

/** The ONE adapter from a listed row to its Data Lake ref (a `folder` is a directory, a `blob` a file); other items have none. */
export function hnsRefOfItem(item: StorageItem): HnsPathRef | undefined {
  if (item.kind === 'folder') return { kind: 'hnsPath', fileSystem: item.container, path: item.prefix.replace(/\/$/u, ''), isDirectory: true };
  if (item.kind === 'blob') return { kind: 'hnsPath', fileSystem: item.container, path: item.name, isDirectory: false };
  return undefined;
}

/** The Blob-view ref of a Data Lake path: the root → `container`, a directory → `blobPrefix` ending in '/', a file → `blob`. */
export function blobViewOfHnsPath(ref: HnsPathRef): Extract<StorageResourceRef, { kind: 'container' | 'blobPrefix' | 'blob' }> {
  if (!ref.isDirectory) return { kind: 'blob', container: ref.fileSystem, name: ref.path };
  return ref.path === '' ? { kind: 'container', container: ref.fileSystem } : { kind: 'blobPrefix', container: ref.fileSystem, prefix: `${ref.path}/` };
}

const stripQuery = (url: string): string => url.split(/[?#]/u)[0];

/** `abfss://<fs>@<dfs host>/<encoded path>` — the account's default dfs host, or the host of a custom dfs endpoint; never a query. */
export function abfssUri(connection: { accountName: string; dfsEndpoint?: string }, ref: HnsPathRef): string {
  const host = connection.dfsEndpoint ? new URL(stripQuery(connection.dfsEndpoint)).host : `${connection.accountName}.dfs.core.windows.net`;
  return `abfss://${ref.fileSystem}@${host}/${ref.path === '' ? '' : renameSegmentsEncoded(ref.path)}`;
}

/** `https://<dfs endpoint>/<fs>/<encoded path>`; a query on the endpoint is dropped (no `sig=` can reach the clipboard). */
export function dfsPathUrl(connection: { dfsEndpoint: string }, ref: HnsPathRef): string {
  const base = stripQuery(connection.dfsEndpoint).replace(/\/+$/u, '');
  const fileSystem = encodeURIComponent(ref.fileSystem);
  return ref.path === '' ? `${base}/${fileSystem}` : `${base}/${fileSystem}/${renameSegmentsEncoded(ref.path)}`;
}
